import React, {useEffect, useRef, useState} from 'react';
import cl from "./FormLinkedItemSelect.module.css";
import {Autocomplete, CircularProgress, TextField} from "@mui/material";
import ServerManager from "../API/ServerManager";
import FormControlCaption from "./FormControlCaption";

const ITEMS_PER_RQ_PAGE = 30;
const TYPING_TIMEOUT = 500; // [ms]

// label - заголовок элемента
// apiName2 - имя соответствующего серверного апи (для получения/фильтрации вариантов). нарпример, "tags", "countries"
// widthLimit - максимально допустимая ширина поля в пикс.
// controlRef - ref, предназначенный для взаимодействия с компонентом (чтение текущих данных, установки ошибки и т.п.)
// showIds - отображать ли помимо имен еще и id опций
const FormInputWithSuggest = ({
                                    label,
                                    initValue,
                                    apiName2,
                                    widthLimit,
                                    controlRef,
                                    showIds,
                                    readOnly,
                                    itemNameFunc,
                                    size = "medium",
                                    placeholder,
                                    tipText,
                                }) => {

    const [errorMessage, setErrorMessage] = useState("")

    const rqIdCounter = useRef(0);
    const [status, setStatus] = useState("closed"); // "", closed, idle, mainLoad, mainError, typing
    const [mainErrorMessage, setMainErrorMessage] = useState(""); // TODO!!
    const [lastTypingTime, setLastTypingTime] = useState(0);
    const [searchQuery, setSearchQuery] = useState("");

    const [needSuggest, setNeedSuggest] = useState(true);

    useEffect(() => {
        if (controlRef) {
            controlRef.current = {
                getText: getText,
                validateNotEmpty: validateNotEmpty,
                validateLength: validateLength,
                setErrorMessage: setErrorMessage,
            };
        }
    })

    function getText() {
        return mainValue.name.trim();
    }

    function validateNotEmpty(errorMessage) {
        if (getText().length > 0)
            return true;
        setErrorMessage(errorMessage);
        return false;
    }

    function validateLength(maxLength, errorMessage = undefined) {
        const textLength = getText().length;
        if (textLength <= maxLength)
            return true;
        setErrorMessage(errorMessage ?? `Слишком длинная строка! (нужно укоротить на ${textLength - maxLength} символ(ов))`);
        return false;
    }

    const correctedWidthLimit = widthLimit === undefined ? 10000 : widthLimit;

    const loadingOption = {id: -1, name: "loading", isLoading: true}
    const hintOption = {id: -2, name: "hint", isHint: true}
    const errorOption = {id: -3, name: "error", isError: true}

    const [mainOptions, setMainOptions] = useState([]);
    const [mainValue, setMainValue] = useState({ id: -10, name: initValue ?? "", isCustom: true});

    function filterOptions(options) {
        if (status === "mainLoad" || status === "typing")
            return [loadingOption];

        return options;
    }

    // только для вывода в input (иначе компонет пытается использовать поле option.label)
    function getOptionLabel(option) {
        if (typeof option === 'string') // TODO?
            return option;

        return option.name;
    }

    // уникальные ключи для каждой опции (по дефолту используются названия опций, что не подходит, если есть одинаковые названия)
    function getOptionKey(option) {
        return option.id;
    }

    function isOptionEqualToValue(option, value) {
        return option.id === value.id;
    }

    function isRegularId(id) {
        return id !== null && id > 0;
    }

    function onFilterEdit(forceUpdate = false) {
        setStatus("typing");
        setLastTypingTime(forceUpdate ? 0 : Date.now());
        setMainOptions([]);
        rqIdCounter.current++;
    }

    async function requestMainItems(searchQuery) {
        rqIdCounter.current++;
        const rqId = rqIdCounter.current;
        //console.log("rqId", rqId);

        setStatus("mainLoad")
        try {
            //await new Promise(resolve => setTimeout(resolve, 1000)); // temp!
            const data = await ServerManager.getSimpleModeFilteredList(apiName2, ITEMS_PER_RQ_PAGE, searchQuery, "name", "asc");
            if (rqIdCounter.current === rqId) {
                const responseList = data[apiName2];
                // TODO? кешировать ответ, если общее кол-во опций мало? (чтобы не переспрашивать одно и то же)

                if (responseList.length === 0)
                    setNeedSuggest(false);

                if (itemNameFunc) {
                    for (const item of responseList)
                        item.name = itemNameFunc(item);
                }

                if (!data.pagination.is_last)
                    responseList.push(hintOption);

                setMainOptions(responseList);
                setStatus("idle");
            } else {
                console.log("rq answer ignored!");
            }
        } catch (error) {
            if (rqIdCounter.current === rqId) {
                setMainErrorMessage(`Ошибка при загрузке данных: ${error}`);
                setStatus("mainError");

                const responseList = [];

                responseList.push(errorOption);

                setMainOptions(responseList);
            }
        } finally {
            if (rqIdCounter.current !== rqId) {
                //console.log("ignored! rqId", rqId);
            }
        }
    }

    // запуск/продление временной задержки во время ввода текста
    useEffect(() => {
        async function wait() {
            const leftTime = lastTypingTime + TYPING_TIMEOUT - Date.now();
            if (leftTime > 0)
                await new Promise(resolve => setTimeout(resolve, leftTime));
            if (!ignore)
                requestMainItems(searchQuery);
        }

        if (status !== "typing")
            return;

        let ignore = false;
        wait();
        return () => {
            ignore = true;
        }

    }, [status, lastTypingTime]);

    return (
        <div className={cl.main}>
            <FormControlCaption
                text={label}
                tipText={tipText}
            />
            <Autocomplete
                freeSolo
                /*selectOnFocus*/
                size={size}
                /*readOnly={readOnly}*/
                renderInput={params => (
                    <TextField
                        {...params}
                        error={errorMessage !== ""}
                        helperText={errorMessage}
                        placeholder={placeholder}
                        slotProps={{
                            htmlInput: {
                                ...params.inputProps,
                                maxLength: 255,
                            },
                            input: {
                                ...params.InputProps,
                                sx: {
                                    ...params.InputProps.sx,
                                    backgroundColor: readOnly ? "#ffff0018" : "white",
                                    borderRadius: "4px",
                                },
                            },
                        }}
                    />
                )}
                options={mainOptions}
                sx={{width: `min(${correctedWidthLimit}px, 100%)`, pointerEvents: readOnly ? "none" : "auto"}}
                disableClearable
                autoHighlight /* при вводе текста сразу подсвечивается первый из доступных вариантов, что позволяет сразу клавишей Enter завершить выбор */
                blurOnSelect /* после выбора, компонент теряет фокус */
                filterOptions={filterOptions}
                noOptionsText="Варианты не найдены"
                getOptionLabel={getOptionLabel}
                clearText="Очистить"
                openText="Открыть список"
                closeText="Закрыть список"
                value={mainValue}
                getOptionKey={getOptionKey}
                isOptionEqualToValue={isOptionEqualToValue}
                getOptionDisabled={(option) => {
                    return option.id < 0
                }}
                onChange={(event, value) => {
                    if (typeof value === 'string')
                        setMainValue({id: -10, name: value, isCustom: true});
                    else
                        setMainValue(value);

                    if (errorMessage)
                        setErrorMessage("");
                }}
                renderOption={(props2, option) => {
                    const {key, ...otherProps} = props2;

                    if (option.isHint) {
                        return (
                            <li key={key} {...otherProps}
                                style={{
                                    display: "block",
                                    fontSize: "14px",
                                    color: "#844",
                                    fontStyle: "italic",
                                    opacity: "0.8"
                                }}>
                                <p style={{marginBottom: "-5px"}}>Показаны не все варианты!</p>
                                <p>Уточните поисковый запрос</p>
                            </li>
                        );
                    }

                    if (option.isLoading) {
                        return (
                            <li key={key} {...otherProps}
                                style={{display: "flex", gap: "10px", color: "#000", opacity: "0.6"}}>
                                <CircularProgress color="inherit" size={20}/>
                                Поиск вариантов...
                            </li>
                        );
                    }

                    if (option.isError) {
                        return (
                            <li key={key} {...otherProps}
                                style={{
                                    display: "block",
                                    fontSize: "14px",
                                    color: "#844",
                                    fontStyle: "italic",
                                    opacity: "0.8"
                                }}>
                                <p style={{marginBottom: "-5px"}}>Не удалось запросить варианты!</p>
                                <p>Попробуйте повторить поиск</p>
                            </li>
                        );
                    }

                    return (
                        <li key={key} {...otherProps}
                            style={{display: "flex", gap: "10px"}}>
                            {option.name}
                            {showIds && isRegularId(option.id) && renderIdInfo(option.id)}
                        </li>
                    );
                }}
                onInputChange={(e, v, r) => {
                    //console.log("onInputChange! v=", v, "r=", r);
                    if (r === "input") {
                        let needSug = needSuggest;
                        if (v.length < mainValue.name.length) {
                            needSug = true;
                            setNeedSuggest(needSug);
                        }
                        setMainValue({id: -10, name: v, isCustom: true});
                        if (needSug) {
                            setSearchQuery(v);
                            onFilterEdit();
                        }
                    }
                }}
                onOpen={() => {
                    if (!mainValue.isNull && needSuggest) {
                        setSearchQuery(mainValue.name);
                        requestMainItems(mainValue.name);
                    }
                }}
                onClose={(event, reason) => {
                    rqIdCounter.current++;
                    setMainOptions([]);
                    setSearchQuery("");
                }}
            />
        </div>
    );
};

function renderIdInfo(id) {
    return <span className={cl.idInfo}>ID {id}</span>
}

export default FormInputWithSuggest;